import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ButtonItem from '../components/ButtonItem';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { GENRES } from '../data/genres';

function AlbumViewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [album, setAlbum] = useState(null);
  const [songs, setSongs] = useState([]);
  const user = useSelector((s) => s.auth?.me);
  const isAdmin = user?.adminYn === 'Y';

  // 별도정산 상태
  const [commissionShares, setCommissionShares] = useState([]);

  const params = new URLSearchParams(location.search);
  const page = params.get('page');
  const search = params.get('search');


  const fetchCommissionShares = useCallback(async () => {
    try {
      const res = await axios.get(`/api/commission-shares?albumId=${id}`);
      setCommissionShares(res.data || []);
    } catch (e) { console.error(e); }
  }, [id]);

  useEffect(() => {
    const fetchAlbum = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/album/${id}`
        );
        setAlbum(response.data);

        // 곡 목록 + songClients 로드
        const songsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/album/${id}/songs`
        );
        setSongs(songsRes.data.items || []);
      } catch (error) {
        console.error('앨범 조회 에러:', error);
      }
    };
    fetchAlbum();
  }, [id]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchCommissionShares();
  }, [isAdmin, fetchCommissionShares]);

  const deleteAlbum = async () => {
    if (window.confirm('정말 이 앨범을 삭제하시겠습니까?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/album/${id}`);
        alert('앨범이 삭제되었습니다.');
        navigate('/Albums');
      } catch (error) {
        alert(
          error.response?.data?.errorMessage || '삭제 중 오류가 발생했습니다.'
        );
      }
    }
  };

  if (!album) {
    return (
      <div className="flex-1 p-8 text-center">로딩 중...</div>
    );
  }

  return (
    <>
        <div className="flex mb-8 justify-center items-center">
          <h1 className="text-xl sm:text-2xl font-bold">앨범 상세정보</h1>
        </div>

        <div className="space-y-6 max-w-2xl mx-auto">
          {/* 권리사 리스트 */}
          <div>
            <label className="block mb-1">
              <span className="font-semibold">권리사명 </span>
              <span className="text-sm text-slate-400">(분배율)</span>
            </label>
            <div className="space-y-2">
              {album.albumClients.map((ac, idx) => (
                <div key={idx} className="flex items-center gap-2 sm:gap-4">
                  <input
                    type="text"
                    value={ac.client.name}
                    readOnly
                    className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-300"
                  />
                  <input
                    type="text"
                    value={`${ac.feeRate}%`}
                    readOnly
                    className="w-20 sm:w-24 px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-right text-slate-300"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* 아티스트 */}
          <div>
            <label className="block font-semibold mb-1">아티스트명</label>
            <input
              type="text"
              value={album.artist.nameKr}
              readOnly
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-300"
            />
          </div>

          {/* 이미지 */}
          <div>
            <label className="block font-semibold mb-1">앨범 이미지</label>
            <img
              src={
                album.image ||
                'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg'
              }
              alt={album.nameKr}
              className="w-48 h-48 rounded-full object-cover mx-auto shadow border mt-2"
            />
          </div>

          {/* 기타 메타데이터 */}
          {[
            { label: '앨범명', value: album.nameKr },
            { label: '앨범코드', value: album.code },
            { label: 'UPC', value: album.upc },
            { label: '바코드', value: album.barcode },
            { label: '발매일', value: album.releaseDateTime ? album.releaseDateTime.split('T')[0] : '' },
            { label: '레이블', value: album.label },
            { label: '수수료율', value: `${album.feeRate}%` },
          ].map((item, idx) => (
            <div key={idx}>
              <label className="block font-semibold mb-1">{item.label}</label>
              <input
                type="text"
                value={item.value || ''}
                readOnly
                className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-300"
              />
            </div>
          ))}

          {/* 장르 */}
          <div>
            <label className="block font-semibold mb-1">장르</label>
            <select
              value={album.genreId}
              disabled
              className="w-full px-2 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-300 focus:outline-none">
              <option value="">장르 선택</option>
              {GENRES.map((g) => (
                <option key={g.id} value={g.id}>{g.label}</option>
              ))}
            </select>
          </div>

          {/* 앨범타입 */}
          <div>
            <label className="block font-semibold mb-1">앨범 타입</label>
            <div className="flex gap-6">
              {['싱글', 'EP', '정규', '컴필레이션'].map((type, i) => (
                <label key={i} className="flex items-center gap-1">
                  <input
                    type="radio"
                    checked={album.albumTypeId === i + 1}
                    disabled
                  />
                  <span>{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 19금 */}
          <div>
            <label className="block font-semibold mb-1">19금 여부</label>
            <div className="flex gap-6">
              <label className="flex items-center gap-1">
                <input type="radio" checked={album.adultYn === 'Y'} disabled />
                <span>19금</span>
              </label>
              <label className="flex items-center gap-1">
                <input type="radio" checked={album.adultYn === 'N'} disabled />
                <span>전체</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block font-semibold mb-1">서비스 지역</label>
            <div className="flex gap-6">
              {['전체', '국내', '해외'].map((s, i) => (
                <label key={i} className="flex items-center gap-1">
                  <input
                    type="radio"
                    checked={album.serviceType === s}
                    disabled
                  />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block font-semibold mb-1">메모</label>
            <textarea
              rows={4}
              value={album.memo || ''}
              readOnly
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-300"
            />
          </div>
        </div>

        {/* 곡별 권리사 설정 현황 */}
        {songs.some((s) => s.songClients?.length > 0) && (
          <div className="max-w-2xl mx-auto mt-10">
            <div className="border border-white/[0.08] rounded-2xl bg-[#182334] overflow-hidden">
              <div className="px-5 py-3 border-b border-white/[0.05]">
                <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest">
                  곡별 권리사 설정
                </h2>
              </div>
              <div className="divide-y divide-white/[0.05]">
                {songs.filter((s) => s.songClients?.length > 0).map((song) => (
                  <div key={song.id} className="px-5 py-3">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs text-slate-500 font-mono w-10">
                        {song.disc}-{String(song.track).padStart(2, '0')}
                      </span>
                      <span className="text-sm text-slate-200 font-medium">
                        {song.nameKr}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium">
                        커스텀
                      </span>
                    </div>
                    <div className="ml-10 space-y-1">
                      {song.songClients.map((sc, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-sm text-slate-300">{sc.client.name}</span>
                          <span className="text-sm text-slate-500">{sc.feeRate}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 별도정산 (관리자 전용 · 읽기 전용) */}
        {isAdmin && commissionShares.length > 0 && (
          <div className="max-w-2xl mx-auto mt-10">
            <div className="border border-amber-500/30 rounded-2xl bg-[#182334] overflow-hidden">
              <div className="px-5 py-3 border-b border-amber-500/20 flex items-center gap-2">
                <h2 className="text-sm font-semibold text-amber-400 uppercase tracking-widest">별도정산</h2>
                <span className="text-xs text-slate-500">관리자 전용 · 권리사 비노출</span>
              </div>
              <div className="p-5 space-y-2">
                {commissionShares.map((cs) => (
                  <div key={cs.id} className="flex items-center gap-3 py-2 px-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    <span className="flex-1 text-sm text-slate-200">{cs.client?.name}</span>
                    <span className="text-amber-400 font-bold text-sm">{cs.rate}%</span>
                    {cs.memo && <span className="text-xs text-slate-500 max-w-[120px] truncate">{cs.memo}</span>}
                  </div>
                ))}
                <div className="text-xs text-slate-500 pt-1">
                  합계: <span className="text-amber-400 font-bold">{commissionShares.reduce((s, c) => s + c.rate, 0)}%</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <ButtonItem
            text="목록"
            color="gray"
            onClick={() => {
              const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
              navigate(`/Albums?page=${page}${searchParam}`);
            }}
          />
          <ButtonItem text="삭제" color="red" onClick={deleteAlbum} />
          <ButtonItem
            text="수정"
            color="blue"
            onClick={() => {
              const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
              navigate(`/Album/Modify/${id}?page=${page}${searchParam}`);
            }}
          />
          <ButtonItem
            text="곡 등록"
            color="lime"
            onClick={() =>
              navigate(`/songWrite?page=${page}`, {
                state: { albumIdToSong: id },
              })
            }
          />
        </div>
    </>
  );
}

export default AlbumViewPage;
